import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Sparkles, Play, Eye, Copy, ChevronDown, ChevronRight, User, StopCircle, Mic, MicOff } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { apiKeyService } from '@/services/apiKeyService';
import type { ApiKey } from '@/components/api-keys/ApiKeysList';
import { RecallioClient, type MemoryRecallRequest, type RecallKnowledgeRequest } from 'recallio';
import { apiJson, apiRaw } from '@/lib/http';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

interface MemoryStep {
  id: string;
  title: string;
  description: string;
  timestamp: Date;
  duration: number;
  status: 'processing' | 'complete';
  icon: React.ReactNode;
}

interface ConversationData {
  inputJson: unknown;
  outputJson: unknown;
}

const Playground = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [memorySteps, setMemorySteps] = useState<MemoryStep[]>([]);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [conversationData, setConversationData] = useState<ConversationData | null>(null);
  const [inputCollapsed, setInputCollapsed] = useState(false);
  const [outputCollapsed, setOutputCollapsed] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [selectedKeyId, setSelectedKeyId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('playground');
  const [userId, setUserId] = useState<string>('');
  const [isMemoryTrailOpen, setIsMemoryTrailOpen] = useState(false);
  const [includeListings, setIncludeListings] = useState<boolean>(true);
  const [includeDocuments, setIncludeDocuments] = useState<boolean>(true);
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const activeRunIdRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const textPcRef = useRef<RTCPeerConnection | null>(null);
  const textDcRef = useRef<RTCDataChannel | null>(null);
  const audioPcRef = useRef<RTCPeerConnection | null>(null);
  const audioDcRef = useRef<RTCDataChannel | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const remoteAudioCtxRef = useRef<AudioContext | null>(null);
  const remoteRafRef = useRef<number | null>(null);
  const isRealtimeActiveRef = useRef(false);
  const toolCallsRef = useRef<Record<string, { name?: string; argsText: string }>>({});
  const [isRealtimeActive, setIsRealtimeActive] = useState(false);
  useEffect(() => { isRealtimeActiveRef.current = isRealtimeActive; }, [isRealtimeActive]);
  // Ensure audio element is muted unless full realtime audio mode is active
  useEffect(() => {
    try {
      if (remoteAudioRef.current) remoteAudioRef.current.muted = !isRealtimeActive;
    } catch { /* noop */ }
  }, [isRealtimeActive]);
  const [realtimePhase, setRealtimePhase] = useState<'idle' | 'listening' | 'connecting' | 'speaking'>('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  const [remoteAudioLevel, setRemoteAudioLevel] = useState(0);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const currentToolCallRef = useRef<{ id?: string; name?: string; argsText: string } | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const serverAttachedRef = useRef<boolean>(false);
  const assistantDraftRef = useRef<string>('');
  const pendingBotMsgIdRef = useRef<string | null>(null);
  const responseIdToMsgIdRef = useRef<Record<string, string>>({});
  const currentResponseIdRef = useRef<string | null>(null);
  const itemIdToMsgIdRef = useRef<Record<string, string>>({});

  const handleToolInvocation = useCallback(async (toolName: string, args: { query?: string; limit?: number }): Promise<string> => {
    try {
      const API_URL = import.meta.env.VITE_API_URL.endsWith('/api') ? import.meta.env.VITE_API_URL.slice(0, -4) : import.meta.env.VITE_API_URL;
      const limit = typeof args.limit === 'number' ? args.limit : 10;
      const query = (args.query || '').toString();
      if (!query) return 'No query provided';

      const activeKey = apiKeys.find(k => k.id === selectedKeyId);
      const authHeaders: Record<string, string> = activeKey?.token ? { 'Authorization': `Bearer ${activeKey.token}` } : {};

      // if (toolName.includes('recall_memory')) {
      //   const payload: { Tags: string[]; Query: string; ProjectId: string, UserId: string } = { Tags: ['chatbot'], Query: query, ProjectId: projectId, UserId: userId };
      //   const res = await fetch(`${API_URL}/api/Memory/recall`, {
      //     method: 'POST',
      //     headers: { 'Content-Type': 'application/json', ...authHeaders },
      //     body: JSON.stringify(payload)
      //   });
      //   if (!res.ok) return `recall_memory failed (${res.status})`;
      //   const json = await res.json() as Array<{ content?: string; Content?: string }>;
      //   const docs = Array.isArray(json) ? json : [];
      //   const text = docs.map((d) => (d?.content ?? d?.Content ?? '')).filter((s): s is string => Boolean(s)).slice(0, limit).join('\n').slice(0, 4000);
      //   return text || 'No results';
      // }

      if (toolName.includes('write_memory')) {
        const payload: { Tags: string[]; Content: string; ProjectId: string; UserId: string; ConsentFlag: boolean; Metadata: Record<string, string> } = { Tags: ['chatbot'], Content: query, ProjectId: projectId, UserId: userId, ConsentFlag: true, Metadata: { "source": "realtime" } };
        const res = await fetch(`${API_URL}/Memory/write`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(payload) });
        if (!res.ok) return `write_memory failed (${res.status})`;
        const json = await res.json() as Array<{ content?: string; Content?: string }>;
        const docs = Array.isArray(json) ? json : [];
        const text = docs.map((d) => (d?.content ?? d?.Content ?? '')).filter((s): s is string => Boolean(s)).slice(0, limit).join('\n');
        return text || 'OK';
      }

      if (toolName.includes('search_knowledge')) {
        type RecallItem = { content?: string; Content?: string; type?: string | null };
        const payload: { Query: string; Type: string; ProjectId: string | undefined; UserId: string } = { Query: query, Type: 'document,livedata,memory', ProjectId: projectId, UserId: userId };
        const res = await fetch(`${API_URL}/Knowledge/recall`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(payload) });
        if (!res.ok) return `search_knowledge failed (${res.status})`;
        const json = await res.json() as Array<RecallItem>;
        const items: RecallItem[] = Array.isArray(json) ? json : [];

        const normalize = (it: RecallItem): { type: string; text: string } => ({
          type: String((it.type || '').toString().toLowerCase() || 'document'),
          text: String(it.content ?? it.Content ?? ''),
        });
        const normalized = items.map(normalize);

        const mem = normalized.filter(i => i.type === 'memory').slice(0, limit);
        const live = normalized.filter(i => i.type === 'livedata').slice(0, limit);
        const docsOnly = normalized.filter(i => i.type === 'document' || (i.type !== 'memory' && i.type !== 'livedata')).slice(0, limit);

        const prettyJson = (s: string): string => {
          try { const obj = JSON.parse(s); return JSON.stringify(obj, null, 2); } catch { return s; }
        };

        const fmtBlock = (label: string, rows: { text: string }[]): string => {
          if (!rows.length) return `${label}: (none)`;
          const body = rows.map(r => `- ${label === 'LIVEDATA' ? prettyJson(r.text) : r.text}`).join('\n');
          return `${label}:\n${body}`;
        };

        const result = [
          fmtBlock('MEMORY', mem),
          fmtBlock('LIVEDATA', live),
          fmtBlock('DOCUMENT', docsOnly),
        ].join('\n\n');

        return result.trim() || 'No results';
      }


      return `Unknown tool: ${toolName}`;
    } catch (e) {
      return `Tool error: ${e instanceof Error ? e.message : 'unknown'}`;
    }
  }, [apiKeys, selectedKeyId, projectId, userId]);

  // Attach standard handlers to our RTC data channel
  const setupDataChannelHandlers = useCallback((dc: RTCDataChannel, kind: 'text' | 'audio') => {
    if (kind === 'text') textDcRef.current = dc; else audioDcRef.current = dc;
    dc.onopen = () => {
      // optional: you can send a session.update here to adjust modalities/instructions
    };
    dc.onclose = () => { /* closed */ };
    dc.onmessage = (evt) => {
      try {
        let text: string | null = null;
        if (typeof evt.data === 'string') text = evt.data as string;
        else if (evt.data instanceof ArrayBuffer) text = new TextDecoder().decode(evt.data);
        if (!text) return;
        const payload = JSON.parse(text) as { type?: string; delta?: string; text?: string; id?: string; tool_call_id?: string; name?: string; response?: { id?: string }; response_id?: string };
        const type: string = payload?.type || '';
        if (type === 'session.created') {
          console.log('session.created', payload);
        } else if (type === 'session.updated') {
          console.log('session.updated', payload);
        } else if (type === 'error') {
          console.log('error', payload);
        } else if (type === 'response.done') {
          //console.log('response.done', payload);
          try {
            const API_URL = import.meta.env.VITE_API_URL.endsWith('/api') ? import.meta.env.VITE_API_URL.slice(0, -4) : import.meta.env.VITE_API_URL;
            const activeKey = apiKeys.find(k => k.id === selectedKeyId);
            const authHeaders: Record<string, string> = activeKey?.token ? { 'Authorization': `Bearer ${activeKey.token}` } : {};
            fetch(`${API_URL}/api/Realtime/statistic`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders },
              body: JSON.stringify({ usage: (payload as { response?: { usage?: object } })?.response?.usage })
            });
          } catch { /* noop */ }
        }
        if (type === 'response.output_text.delta' || type === 'response.delta') {
          const rid = (payload as { response_id?: string })?.response_id || (payload as { response?: { id?: string } })?.response?.id || currentResponseIdRef.current || '';
          if (rid) {
            let msgId = responseIdToMsgIdRef.current[rid] || pendingBotMsgIdRef.current || '';
            if (!msgId) {
              msgId = `${Date.now()}_bot_rtc`;
              responseIdToMsgIdRef.current[rid] = msgId;
              pendingBotMsgIdRef.current = msgId;
              assistantDraftRef.current = '';
              setMessages(prev => [...prev, { id: msgId, type: 'bot', content: '', timestamp: new Date() }]);
            }
            let chunk = '';
            if (type === 'response.output_text.delta') {
              // OpenAI sends { delta: string }
              const d = (payload as { delta?: unknown; text?: string }).delta;
              if (typeof d === 'string') chunk = d;
              else if (typeof (payload as { text?: string }).text === 'string') chunk = (payload as { text?: string }).text as string;
            } else if (type === 'response.delta') {
              // Nested delta object: { delta: { type: 'output_text.delta', text: '...' } }
              const d = (payload as { delta?: unknown }).delta as unknown;
              if (typeof d === 'string') chunk = d as string;
              else if (d && typeof d === 'object' && typeof (d as { text?: string }).text === 'string') chunk = (d as { text?: string }).text as string;
            }
            if (chunk) {
              assistantDraftRef.current += chunk;
              const content = assistantDraftRef.current;
              setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content } : m));
            }
          }
        }
        if (type === 'response.output_text.done' || type === 'response.completed') {
          const rid = (payload as { response_id?: string })?.response_id || (payload as { response?: { id?: string } })?.response?.id || currentResponseIdRef.current || '';
          if (rid) {
            const msgId = responseIdToMsgIdRef.current[rid] || pendingBotMsgIdRef.current;
            if (msgId) {
              assistantDraftRef.current = '';
              delete responseIdToMsgIdRef.current[rid];
              if (currentResponseIdRef.current === rid) currentResponseIdRef.current = null;
              pendingBotMsgIdRef.current = null;
            }
          }
        }

        // Tool calling support
        if ((type.includes('response.tool_call') || type.includes('response.tool_calls.created')) && (payload as { id?: string }).id) {
          const pid = (payload as { id?: string }).id as string;
          toolCallsRef.current[pid] = { name: (payload as { name?: string }).name, argsText: '' };
          currentToolCallRef.current = { id: pid, name: (payload as { name?: string }).name, argsText: '' };
        }
        if ((type.includes('response.function_call_arguments.delta') || type === 'response.function_call_arguments.delta') && typeof payload?.delta === 'string') {
          const callId: string | undefined = (payload as { call_id?: string })?.call_id || currentToolCallRef.current?.id;
          if (callId) {
            if (!toolCallsRef.current[callId]) toolCallsRef.current[callId] = { argsText: '' } as { name?: string; argsText: string };
            toolCallsRef.current[callId].argsText += payload.delta;
            currentToolCallRef.current = { ...(toolCallsRef.current[callId] || { argsText: '' }), id: callId } as { id?: string; name?: string; argsText: string };
          }
        }
        if (type.includes('response.function_call_arguments.done') || type === 'response.function_call_arguments.done') {
          const callId: string | undefined = (payload as { call_id?: string })?.call_id || currentToolCallRef.current?.id;
          const tool = callId ? toolCallsRef.current[callId] : currentToolCallRef.current;
          try {
            if (tool) {
              const argsText = (tool.argsText && tool.argsText.trim().length > 0) ? tool.argsText : (payload as { arguments?: string })?.arguments || '';
              const args = JSON.parse(argsText || '{}') as { name?: string; query?: string; limit?: number };
              const toolName = ((payload as { name?: string })?.name || args.name || '').toLowerCase();
              if (toolName) {
                handleToolInvocation(toolName, args).then(async (output) => {
                  try {
                    if (callId) {
                      const out = typeof output === 'string' ? output : JSON.stringify(output);
                      (kind === 'text' ? textDcRef.current : audioDcRef.current)?.send(JSON.stringify({
                        type: "conversation.item.create",
                        item: { type: "function_call_output", call_id: callId, output: out }
                      }));
                      (kind === 'text' ? textDcRef.current : audioDcRef.current)?.send(JSON.stringify({ type: 'response.create' }));
                    }
                  } catch { /* noop */ }
                }).catch((_e) => { /* noop */ });
              }
            }
          } catch { /* noop */ }
          if (callId && toolCallsRef.current[callId]) delete toolCallsRef.current[callId];
          currentToolCallRef.current = null;
        }
      } catch (_e) { /* ignore parsing */ }
    };
  }, [handleToolInvocation]);

  const notifyServerAttach = useCallback(async (controlUrl: string, clientSecret: string) => {
    if (serverAttachedRef.current) return;
    serverAttachedRef.current = true;
    try {
      const API_URL = import.meta.env.VITE_API_URL.endsWith('/api') ? import.meta.env.VITE_API_URL.slice(0, -4) : import.meta.env.VITE_API_URL;
      const activeKey = apiKeys.find(k => k.id === selectedKeyId);
      const authHeaders: Record<string, string> = activeKey?.token ? { 'Authorization': `Bearer ${activeKey.token}` } : {};
      await fetch(`${API_URL}/api/Realtime/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ controlUrl, clientSecret })
      });
    } catch { /* noop */ }
  }, [apiKeys, selectedKeyId]);

  // Create a text-only Realtime session (no mic, no UI mode toggles)
  const ensureTextRealtimeSession = useCallback(async () => {
    if (textDcRef.current && textDcRef.current.readyState === 'open') return;
    try { textPcRef.current?.close(); } catch { /* noop */ }
    const pc = new RTCPeerConnection();
    textPcRef.current = pc;
    // Provide an audio m-section but do not receive audio in text mode
    try { pc.addTransceiver('audio', { direction: 'inactive' }); } catch { /* noop */ }
    pc.ontrack = () => { /* no audio in text mode */ };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        try { textDcRef.current?.close(); } catch { /* noop */ }
        textDcRef.current = null;
      }
    };
    const dc = pc.createDataChannel('oai-events');
    setupDataChannelHandlers(dc, 'text');

    const activeKey = apiKeys.find(k => k.id === selectedKeyId);
    const authHeaders: Record<string, string> = activeKey?.token ? { 'Authorization': `Bearer ${activeKey.token}` } : {};
    const session = await apiJson<{ clientSecret: string; session: object; }>(
      '/Realtime/session',
      { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders } }
    );

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const fd = new FormData();
    fd.set('sdp', offer.sdp);
    session.session['output_modalities'] = ['text'];
    console.log('session.session', session.session);
    fd.set('session', JSON.stringify(session.session));
    const res = await fetch('https://api.openai.com/v1/realtime/calls', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.clientSecret}` },
      body: fd
    });
    if (!res.ok) throw new Error(`OpenAI SDP exchange failed (${res.status})`);
    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    const controlUrl = res.headers.get('Location');
    if (controlUrl) await notifyServerAttach(controlUrl, session.clientSecret);

    // // Ensure text-only responses for this session
    // try {
    //   const chan = textDcRef.current;
    //   const sendTextOnly = () => {
    //     try {
    //       chan?.send(JSON.stringify({ type: 'session.update', session: { output_modalities: ['text'] } }));
    //     } catch { /* noop */ }
    //   };
    //   if (chan?.readyState === 'open') sendTextOnly();
    //   else chan?.addEventListener?.('open', () => sendTextOnly(), { once: true } as unknown as AddEventListenerOptions);
    // } catch { /* noop */ }
  }, [apiKeys, selectedKeyId, notifyServerAttach, setupDataChannelHandlers]);



  const muteRealtimeSession = () => {
    try {
      if (!pcRef.current) return;
      const senders = pcRef.current.getSenders().filter(s => s.track && s.track.kind === 'audio');
      if (!senders.length) return;
      const muted = !isMuted;
      setIsMuted(muted);
      senders.forEach(s => { if (s.track) s.track.enabled = !muted; });
      // Also toggle micStream for local UI meter
      if (micStreamRef.current) {
        micStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !muted; });
      }
    } catch { /* noop */ }
  };

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Initialize with sample conversation
  useEffect(() => {
    //     const initialMessages: Message[] = [
    //       {
    //         id: '1',
    //         type: 'user',
    //         content: 'What pending tasks do I have at 742 Evergreen Terrace?',
    //         timestamp: new Date(Date.now() - 120000)
    //       },
    //       {
    //         id: '2',
    //         type: 'bot',
    //         content: `Here are the 3 pending tasks for 742 Evergreen Terrace, Springfield, IL 62704:

    // • **Roof inspection** — scheduled but not yet completed (due Sept 12, 2025).
    // • **HVAC filter replacement** — work order created, still pending.
    // • **Legal compliance report submission** — draft uploaded, not yet filed with city records.

    // All tasks are accessible through your property management dashboard. Would you like me to help prioritize these or provide more details about any specific task?`,
    //         timestamp: new Date(Date.now() - 115000)
    //       }
    //     ];

    // const initialSteps: MemoryStep[] = [
    //   {
    //     id: '1',
    //     title: 'Query Analysis',
    //     description: 'Extracting key entities: property address, task status',
    //     timestamp: new Date(Date.now() - 118000),
    //     duration: 120,
    //     status: 'complete',
    //     icon: <Sparkles className="h-4 w-4 text-recallio-yellow" />
    //   },
    //   {
    //     id: '2',
    //     title: 'Memory Retrieval',
    //     description: 'Searching property management records and task databases',
    //     timestamp: new Date(Date.now() - 117000),
    //     duration: 340,
    //     status: 'complete',
    //     icon: <Sparkles className="h-4 w-4 text-recallio-green" />
    //   },
    //   {
    //     id: '3',
    //     title: 'Context Synthesis',
    //     description: 'Filtering relevant memories and cross-referencing property data',
    //     timestamp: new Date(Date.now() - 116500),
    //     duration: 180,
    //     status: 'complete',
    //     icon: <Sparkles className="h-4 w-4 text-recallio-yellow" />
    //   },
    //   {
    //     id: '4',
    //     title: 'TTL Validation',
    //     description: 'Checking memory freshness and data validity',
    //     timestamp: new Date(Date.now() - 116000),
    //     duration: 95,
    //     status: 'complete',
    //     icon: <Sparkles className="h-4 w-4 text-recallio-green" />
    //   },
    //   {
    //     id: '5',
    //     title: 'Response Generation',
    //     description: 'Synthesizing coherent response from verified memory context',
    //     timestamp: new Date(Date.now() - 115500),
    //     duration: 245,
    //     status: 'complete',
    //     icon: <Sparkles className="h-4 w-4 text-recallio-yellow" />
    //   }
    // ];

    //setMessages(initialMessages);
    //setMemorySteps(initialSteps);

    // Set mock conversation data
    //   setConversationData({
    //     inputJson: {
    //       query: "What pending tasks do I have at 742 Evergreen Terrace?",
    //       user_context: {
    //         user_id: "user_12345",
    //         session_id: "sess_abcd1234",
    //         timestamp: "2024-12-30T10:30:00Z"
    //       },
    //       memory_context: [
    //         {
    //           id: "mem_001",
    //           type: "property_record",
    //           content: "742 Evergreen Terrace, Springfield, IL 62704 - Property Management Account",
    //           metadata: {
    //             property_id: "prop_742_evergreen",
    //             last_updated: "2024-12-29T15:20:00Z",
    //             ttl: "2025-03-30T00:00:00Z"
    //           }
    //         },
    //         {
    //           id: "mem_002",
    //           type: "task_record",
    //           content: "Roof inspection scheduled for Sept 12, 2025 - Status: Pending",
    //           metadata: {
    //             task_id: "task_roof_001",
    //             priority: "high",
    //             due_date: "2025-09-12"
    //           }
    //         }
    //       ]
    //     },
    //     outputJson: {
    //       response: "Here are the 3 pending tasks for 742 Evergreen Terrace, Springfield, IL 62704...",
    //       confidence: 0.92,
    //       sources_used: [
    //         "mem_001", "mem_002", "mem_003"
    //       ],
    //       reasoning_chain: [
    //         "Identified property: 742 Evergreen Terrace",
    //         "Retrieved property management records",
    //         "Filtered for pending tasks only",
    //         "Cross-referenced with current date",
    //         "Formatted response with task details"
    //       ],
    //       metadata: {
    //         processing_time_ms: 1180,
    //         memories_accessed: 3,
    //         memories_filtered: 8,
    //         response_type: "structured_list"
    //       }
    //     }
    //   });
  }, []);

  // Load API keys and select first by default
  useEffect(() => {
    const loadKeys = async () => {
      try {
        const keys = await apiKeyService.getApiKeys();
        const activeKeys = keys.filter(k => k.status === 'active');
        setApiKeys(activeKeys);
        if (activeKeys.length > 0) {
          setSelectedKeyId(activeKeys[0].id);
        }
      } catch (err) {
        console.error('Failed to load API keys', err);
      }
    };
    loadKeys();
  }, []);

  // Initialize default userId from logged in user first name
  useEffect(() => {
    if (user?.name) {
      const firstName = user.name.trim().split(' ')[0] || user.name;
      setUserId(firstName);
    }
  }, [user]);

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;

    // Prefer audio DC if active; otherwise ensure text DC
    try {
      const activeAudioDcOpen = isRealtimeActive && audioDcRef.current && audioDcRef.current.readyState === 'open';
      if (!activeAudioDcOpen && (!textDcRef.current || textDcRef.current.readyState !== 'open')) {
        await ensureTextRealtimeSession();
        await waitForDataChannelOpen(8000);
      }
      const dc = activeAudioDcOpen ? audioDcRef.current : textDcRef.current;
      if (dc && dc.readyState === 'open') {
        await sendTextViaRealtime(currentMessage.trim());
        setCurrentMessage('');
        return;
      }
    } catch { /* fall back to HTTP flow */ }

    // At this point, if RTC send failed we stop here and do not call HTTP autocomplete
  };

  // Send a user text message via the Realtime data channel and prompt a response
  const sendTextViaRealtime = async (text: string) => {
    try {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Append user message locally
      const userMessage: Message = { id: `${Date.now()}_user_rtc`, type: 'user', content: trimmed, timestamp: new Date() };
      setMessages(prev => [...prev, userMessage]);

      const dc = (isRealtimeActive && audioDcRef.current && audioDcRef.current.readyState === 'open')
        ? audioDcRef.current
        : textDcRef.current;
      if (!dc || dc.readyState !== 'open') return;
      // Create conversation item for user text
      dc.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [
            { type: 'input_text', text: trimmed }
          ]
        }
      }));
      // Ask the model to respond; prefer text modality on text DC
      if (dc === textDcRef.current) dc.send(JSON.stringify({ type: 'response.create' }));
      else dc.send(JSON.stringify({ type: 'response.create' }));
    } catch { /* noop */ }
  };

  // Wait for RTC data channel to open
  const waitForDataChannelOpen = async (timeoutMs: number = 8000): Promise<void> => {
    const dc = textDcRef.current;
    if (dc && dc.readyState === 'open') return;
    await new Promise<void>((resolve, reject) => {
      const chan = textDcRef.current;
      if (!chan) return reject(new Error('No data channel'));
      const timer = setTimeout(() => {
        try { chan.removeEventListener?.('open', onOpen as EventListener); } catch { /* noop */ }
        reject(new Error('Data channel open timeout'));
      }, timeoutMs);
      function onOpen() {
        clearTimeout(timer);
        try { chan.removeEventListener?.('open', onOpen as EventListener); } catch { /* noop */ }
        resolve();
      }
      try { chan.addEventListener?.('open', onOpen as EventListener); } catch { /* noop */ }
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const isRunActive = (runId: number) => activeRunIdRef.current === runId;

  const handleCancel = () => {
    if (!isLoading) return;
    activeRunIdRef.current = null;
    try {
      abortControllerRef.current?.abort();
    } catch (e) { /* noop */ }
    abortControllerRef.current = null;
    setIsLoading(false);
    setMemorySteps([]);
  };

  const handleDeleteMemories = async () => {
    if (isDeleting) return;
    try {
      const activeKey = apiKeys.find(k => k.id === selectedKeyId);
      if (!activeKey?.token) {
        throw new Error('No active API key selected');
      }
      if (!projectId || !userId) {
        throw new Error('Project ID and User ID are required');
      }

      const confirmed = window.confirm(`Delete memories for user "${userId}" in project "${projectId}"? This cannot be undone.`);
      if (!confirmed) return;

      setIsDeleting(true);
      const client = new RecallioClient({ apiKey: activeKey.token });
      await client.deleteMemory({ scope: 'user', userId, projectId });
      toast.success('Memories deleted');
    } catch (err) {
      console.error('deleteMemory failed', err);
      toast.error('Failed to delete memories');
    } finally {
      setIsDeleting(false);
    }
  };

  // ========= Realtime (WebRTC) =========
  const refreshAudioDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(d => d.kind === 'audioinput');
      setAudioDevices(inputs);
      if (inputs.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(inputs[0].deviceId);
      }
    } catch (e) {
      console.debug('enumerateDevices failed', e);
    }
  }, [selectedDeviceId]);

  useEffect(() => {
    refreshAudioDevices();
    const onChange = () => { refreshAudioDevices(); };
    try { navigator.mediaDevices?.addEventListener?.('devicechange', onChange); } catch (_e) { /* noop */ }
    return () => { try { navigator.mediaDevices?.removeEventListener?.('devicechange', onChange); } catch (_e) { /* noop */ } };
  }, [refreshAudioDevices]);

  const startRealtimeSession = async () => {
    if (isRealtimeActive) return;
    try {
      // Request mic
      const constraints: MediaStreamConstraints = { audio: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true };
      let micStream: MediaStream;
      try {
        micStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err: unknown) {
        const errName = (err as { name?: string } | null | undefined)?.name;
        if (selectedDeviceId && (errName === 'NotFoundError' || errName === 'OverconstrainedError')) {
          // Retry without device constraint
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
          throw err;
        }
      }
      micStreamRef.current = micStream;
      setRealtimePhase('listening');

      // Create RTCPeerConnection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          stopRealtimeSession();
        }
        // Do not auto-stop on 'disconnected': allow brief network hiccups
      };
      pc.ontrack = (event) => {
        try {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = event.streams[0];
          }
          // Setup remote analyser once
          if (!remoteAnalyserRef.current && event.streams && event.streams[0]) {
            try {
              const AudioCtx: typeof AudioContext = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
                || (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).webkitAudioContext!;
              const ctx = new AudioCtx();
              const source = ctx.createMediaStreamSource(event.streams[0]);
              const analyser = ctx.createAnalyser();
              analyser.fftSize = 512;
              analyser.smoothingTimeConstant = 0.85;
              source.connect(analyser);
              remoteAudioCtxRef.current = ctx;
              remoteAnalyserRef.current = analyser;
              const data = new Uint8Array(analyser.frequencyBinCount);
              const tick = () => {
                if (!remoteAnalyserRef.current) return;
                remoteAnalyserRef.current.getByteTimeDomainData(data);
                let sum = 0;
                for (let i = 0; i < data.length; i++) {
                  const v = (data[i] - 128) / 128;
                  sum += v * v;
                }
                const rms = Math.sqrt(sum / data.length); // 0..1
                setRemoteAudioLevel(rms);
                remoteRafRef.current = requestAnimationFrame(tick);
              };
              remoteRafRef.current = requestAnimationFrame(tick);
            } catch (_e) { /* noop */ }
          }
        } catch (e) { /* noop */ }
      };

      // Send + receive audio
      micStream.getTracks().forEach(track => pc.addTrack(track, micStream));
      pc.addTransceiver('audio', { direction: 'sendrecv' });

      // Optional: data channel for events
      const dc = pc.createDataChannel('oai-events');
      setupDataChannelHandlers(dc, 'audio');
      dc.onopen = () => { };
      dc.onclose = () => { /* closed */ };

      // Setup microphone analyser for UI
      try {
        const AudioCtx: typeof AudioContext = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
          || (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).webkitAudioContext!;
        const ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(micStream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.85;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length); // 0..1
          setAudioLevel(rms);
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        console.debug('Audio analyser init failed', e);
      }

      // Reflect speaking state from remote audio element
      try {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.onplay = () => setRealtimePhase('speaking');
          remoteAudioRef.current.onended = () => setRealtimePhase('listening');
          remoteAudioRef.current.onpause = () => setRealtimePhase('listening');
        }
      } catch (_e) { /* noop */ }



      // Create ephemeral session token from our server
      const activeKey = apiKeys.find(k => k.id === selectedKeyId);
      const authHeaders: Record<string, string> = activeKey?.token ? { 'Authorization': `Bearer ${activeKey.token}` } : {};
      const session = await apiJson<{ clientSecret: string; session: object; }>(
        '/Realtime/session',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders }
        }
      );

      // Create SDP offer
      setRealtimePhase('connecting');
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      const fd = new FormData();
      fd.set("sdp", offer.sdp);
      session.session['output_modalities'] = ['audio'];
      fd.set("session", JSON.stringify(session.session));

      const res = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: { Authorization: `Bearer ${session.clientSecret}` }, // ek_…
        body: fd // let the browser set multipart/form-data; boundary
      });


      if (!res.ok) {
        throw new Error(`OpenAI SDP exchange failed (${res.status})`);
      }
      const answerSdp = await res.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      const controlUrl = res.headers.get("Location");   // CORS exposes this
      if (controlUrl) {
        await notifyServerAttach(controlUrl, session.clientSecret);       // send to your backend
      }

      setIsRealtimeActive(true);
      setRealtimePhase('listening');
      //toast.success('Realtime session started');
    } catch (err: unknown) {
      console.error('Failed to start realtime session', err);
      if ((err as { name?: string } | null | undefined)?.name === 'NotAllowedError') {
        toast.error('Microphone permission denied. Please allow mic access in your browser.');
      } else if ((err as { name?: string } | null | undefined)?.name === 'NotFoundError') {
        toast.error('No microphone found. Connect a microphone or choose a different input device.');
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to start realtime session');
      }
      stopRealtimeSession();
    }
  };


  const stopRealtimeSession = useCallback(async () => {


    try { dataChannelRef.current?.close(); } catch (_e) { void 0; }
    dataChannelRef.current = null;
    try { pcRef.current?.close(); } catch (_e) { void 0; }
    pcRef.current = null;
    try {
      micStreamRef.current?.getTracks().forEach(t => {
        try { t.stop(); } catch (_e) { void 0; }
      });
    } catch (_e) { void 0; }
    micStreamRef.current = null;
    if (rafRef.current) {
      try { cancelAnimationFrame(rafRef.current); } catch (_e) { /* noop */ }
      rafRef.current = null;
    }
    try { analyserRef.current?.disconnect(); } catch (_e) { /* noop */ }
    analyserRef.current = null;
    try { audioCtxRef.current?.close(); } catch (_e) { /* noop */ }
    audioCtxRef.current = null;
    if (remoteRafRef.current) {
      try { cancelAnimationFrame(remoteRafRef.current); } catch (_e) { /* noop */ }
      remoteRafRef.current = null;
    }
    try { remoteAnalyserRef.current?.disconnect(); } catch (_e) { /* noop */ }
    remoteAnalyserRef.current = null;
    try { remoteAudioCtxRef.current?.close(); } catch (_e) { /* noop */ }
    remoteAudioCtxRef.current = null;

    setIsRealtimeActive(false);
    setRealtimePhase('idle');
    setAudioLevel(0);
    setRemoteAudioLevel(0);
  }, []);

  useEffect(() => {
    return () => {
      stopRealtimeSession();
    };
  }, [stopRealtimeSession]);

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const MemoryTrailBody = () => (
    <div className="space-y-3">
      {memorySteps.map((step) => (
        <div key={step.id} className={cn(
          "p-3 rounded-lg border transition-all duration-200",
          step.status === 'processing' ? "bg-accent/50 border-recallio-yellow/30" : "bg-card border-border"
        )}>
          <div className="flex items-center gap-2 mb-1">
            {step.icon}
            <span className="text-sm font-medium">{step.title}</span>
            <Badge variant="outline" className="ml-auto text-xs">
              {step.duration}ms
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground mb-1">
            {step.description}
            {step.status === 'processing' && (
              <div className="flex items-center mt-2 space-x-1">
                <span className="w-2 h-2 rounded-full bg-recallio-yellow animate-bounce" style={{ animationDelay: '0s' }} />
                <span className="w-2 h-2 rounded-full bg-recallio-yellow animate-bounce" style={{ animationDelay: '0.1s' }} />
                <span className="w-2 h-2 rounded-full bg-recallio-yellow animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTimestamp(step.timestamp)}
          </div>
        </div>
      ))}

      {memorySteps.length > 0 && !isLoading && (
        <div className="p-3 rounded-lg bg-recallio-green/10 border border-recallio-green/30 mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-recallio-green" />
            <span className="text-sm font-medium text-recallio-green">Response ready</span>
          </div>

          <Dialog open={showJsonModal} onOpenChange={setShowJsonModal}>
            <DialogTrigger asChild>
              <Button size="sm" className="w-full">
                <Eye className="h-3 w-3 mr-1" />
                View JSON
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle>Memory Processing Data</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <Collapsible open={!inputCollapsed} onOpenChange={setInputCollapsed}>
                    <CollapsibleTrigger className="flex items-center gap-2 p-3 hover:bg-accent rounded-lg w-full">
                      {inputCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <span className="font-medium">Input JSON</span>
                      <Badge variant="outline" className="ml-auto">
                        Query & Context
                      </Badge>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="relative">
                        <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto font-mono">
                          {JSON.stringify(conversationData?.inputJson, null, 2)}
                        </pre>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => copyToClipboard(JSON.stringify(conversationData?.inputJson, null, 2))}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>

                <div>
                  <Collapsible open={!outputCollapsed} onOpenChange={setOutputCollapsed}>
                    <CollapsibleTrigger className="flex items-center gap-2 p-3 hover:bg-accent rounded-lg w-full">
                      {outputCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <span className="font-medium">Output JSON</span>
                      <Badge variant="outline" className="ml-auto">
                        Response & Reasoning
                      </Badge>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="relative">
                        <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto font-mono">
                          {JSON.stringify(conversationData?.outputJson, null, 2)}
                        </pre>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => copyToClipboard(JSON.stringify(conversationData?.outputJson, null, 2))}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );

  const DialogPanel = () => (
    <div className="flex-1 overflow-y-auto mb-4 pr-2 flex items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <div
          className={cn(
            "relative flex items-center justify-center w-32 h-32 rounded-full",
            realtimePhase === 'listening' && "bg-blue-200",
            realtimePhase === 'connecting' && "bg-yellow-200",
            realtimePhase === 'speaking' && "bg-green-200",
            realtimePhase === 'idle' && "bg-muted"
          )}
          style={{
            transform: `scale(${1 + Math.min(0.2, remoteAudioLevel * 0.8)})`,
            transition: 'transform 80ms ease-out'
          }}
        >
          {/* Pulse ring */}
          {(realtimePhase === 'listening' || realtimePhase === 'connecting') && (
            <span className="absolute inline-flex h-full w-full rounded-full bg-current opacity-30 animate-ping" />
          )}
          <div
            className={cn(
              "z-10 w-24 h-24 rounded-full overflow-hidden flex items-center justify-center border",
              realtimePhase === 'listening' && "bg-white border-blue-300",
              realtimePhase === 'connecting' && "bg-white border-yellow-300",
              realtimePhase === 'speaking' && "bg-white border-green-300",
              realtimePhase === 'idle' && "bg-white border"
            )}
            style={{
              transform: `scale(${1 + Math.min(0.25, remoteAudioLevel)})`,
              transition: 'transform 60ms ease-out'
            }}
          >
            <img src="/logo_recallio.png" alt="Recallio" className="w-full h-full object-cover rounded-full" />

          </div>
        </div>

        {/* Mode label */}
        <div className="text-sm text-muted-foreground capitalize">
          {realtimePhase === 'connecting' ? 'connecting…' : realtimePhase}
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout title="Playground" description="Test queries against your memory infrastructure and observe real-time processing">
      <div className="mb-4">
        <Card className="border border-recallio-light-gray/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Request Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <Label className="mb-2 block">API Key</Label>
                <Select value={selectedKeyId} onValueChange={setSelectedKeyId} disabled={apiKeys.length === 0}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a key" />
                  </SelectTrigger>
                  <SelectContent>
                    {apiKeys.map((k) => (
                      <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="projectId" className="mb-2 block">Project ID</Label>
                <Input id="projectId" value={projectId} onChange={(e) => setProjectId(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="userId" className="mb-2 block">User ID</Label>
                <Input id="userId" value={userId} onChange={(e) => setUserId(e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="includeListings" checked={includeListings} onCheckedChange={(v) => setIncludeListings(Boolean(v))} />
                <Label htmlFor="includeListings">Include Listings</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="includeDocuments" checked={includeDocuments} onCheckedChange={(v) => setIncludeDocuments(Boolean(v))} />
                <Label htmlFor="includeDocuments">Include Documents</Label>
              </div>
              <div className="lg:col-span-3">
                <Button
                  variant="destructive"
                  onClick={handleDeleteMemories}
                  disabled={isDeleting || apiKeys.length === 0 || !selectedKeyId || !projectId || !userId}
                >
                  {isDeleting ? 'Deleting…' : 'Delete Memories'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="flex gap-6 h-[calc(100vh-12rem)] min-h-[600px]">
        {/* Left Panel - Chatbot Demo */}
        <Card className="flex-1 flex flex-col">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5 text-recallio-green" />
                Chatbot Demo
              </CardTitle>
              <div className="flex items-center gap-3">
                {/* Audio element for assistant speech (keep here to avoid stopping playback) */}
                <audio ref={remoteAudioRef} autoPlay playsInline />
              </div>
              <Sheet open={isMemoryTrailOpen} onOpenChange={setIsMemoryTrailOpen}>
                <SheetTrigger asChild>
                  <Button size="sm" variant="secondary" className="md:hidden">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Memory Trail
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[90vw] sm:w-[420px]">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-recallio-yellow" />
                      Memory Trail
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-4 h-[80vh] overflow-y-auto">
                    <MemoryTrailBody />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </CardHeader>

          <CardContent className="flex-1 flex flex-col min-h-0">
            {/* Messages area OR Dialog panel */}
            {(isRealtimeActive || realtimePhase !== 'idle') ? (
              <DialogPanel />
            ) : (
              <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
                {messages.map((message) => (
                  <div key={message.id} className={cn(
                    "flex gap-3",
                    message.type === 'user' ? "justify-end" : "justify-start"
                  )}>
                    {message.type === 'bot' && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-recallio-green flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-recallio-background" />
                      </div>
                    )}

                    <div className={cn(
                      "max-w-[80%] rounded-lg px-4 py-3 break-words",
                      message.type === 'user'
                        ? "bg-recallio-yellow text-recallio-background ml-auto"
                        : "bg-muted text-foreground"
                    )}>
                      <div className="whitespace-pre-wrap text-sm leading-relaxed">
                        {message.content}
                      </div>
                      <div className={cn(
                        "text-xs mt-2 opacity-70",
                        message.type === 'user' ? "text-recallio-background" : "text-muted-foreground"
                      )}>
                        {formatTimestamp(message.timestamp)}
                      </div>
                    </div>

                    {message.type === 'user' && (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-recallio-yellow flex items-center justify-center">
                        <User className="h-4 w-4 text-recallio-background" />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />

                {/* Typing indicator */}
                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-recallio-green flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-recallio-background animate-pulse" />
                    </div>
                    <div className="bg-muted rounded-lg px-4 py-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-recallio-green rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-recallio-green rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-recallio-green rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Input area (replace with status + visualizer while realtime is active) */}
            {isRealtimeActive ? (
              <div className="flex items-center justify-between gap-3">
                <div className={cn(
                  "flex items-center gap-2 rounded-full px-3 py-1 text-xs border",
                  realtimePhase === 'listening' && "bg-blue-50 border-blue-200 text-blue-700",
                  realtimePhase === 'connecting' && "bg-yellow-50 border-yellow-200 text-yellow-700",
                  realtimePhase === 'speaking' && "bg-green-50 border-green-200 text-green-700",
                  realtimePhase === 'idle' && "bg-muted border-border text-muted-foreground"
                )}>
                  {realtimePhase === 'listening' && <span className="inline-flex w-2 h-2 rounded-full bg-blue-500 animate-pulse" />}
                  {realtimePhase === 'connecting' && <span className="inline-flex w-2 h-2 rounded-full bg-yellow-500 animate-ping" />}
                  {realtimePhase === 'speaking' && <span className="inline-flex w-2 h-2 rounded-full bg-green-500 animate-bounce" />}
                  <span className="capitalize">{realtimePhase}</span>
                </div>
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 flex items-end justify-between h-5 overflow-hidden" aria-hidden>
                    {Array.from({ length: 25 }).map((_, i) => {
                      const base = audioLevel * 20; // scale
                      const jitter = ((i * 13) % 7) * 0.5;
                      const h = Math.max(2, Math.min(20, Math.round(base + jitter)));
                      return (
                        <span key={i} className="w-[3px] bg-current rounded-sm" style={{ height: `${h}px` }} />
                      );
                    })}
                  </div>
                  <Button size="icon" variant={isMuted ? "destructive" : "secondary"} onClick={muteRealtimeSession} title={isMuted ? "Unmute" : "Mute"} aria-pressed={isMuted}>
                    {isMuted ? (
                      <Mic className="h-4 w-4" />
                    ) : (
                      <MicOff className="h-4 w-4" />
                    )}
                  </Button>
                  <Button size="icon" variant="secondary" onClick={stopRealtimeSession} title="Stop Mic">
                    <StopCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}

            {isRealtimeActive ? (
              <div className="flex gap-2 items-center mt-2">
                <Input
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  placeholder="Type to chat via realtime…"
                  disabled={false}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!currentMessage.trim() || apiKeys.length === 0 || !selectedKeyId}
                  size="icon"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <Input
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    placeholder="Ask a question..."
                    disabled={isLoading}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="flex-1"
                  />
                  {isLoading ? (
                    <Button
                      onClick={handleCancel}
                      variant="destructive"
                      size="icon"
                    >
                      <StopCircle className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSendMessage}
                      disabled={!currentMessage.trim() || apiKeys.length === 0 || !selectedKeyId}
                      size="icon"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="icon" variant="secondary" onClick={startRealtimeSession} title="Start Mic">
                    <Mic className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Right Panel - Memory Trail */}
        <Card className="w-96 md:max-w-[50%] flex flex-col hidden md:flex">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-recallio-yellow" />
              Memory Trail
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-1 overflow-y-auto">
            <MemoryTrailBody />
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Playground;